/**
 * ConnectFourEngine - Core game logic for Connect Four
 *
 * Manages game state, validates moves, detects wins/draws, and enforces rules.
 * Grid convention: grid[row][col], row 0 = bottom, row 5 = top.
 *
 * Requirements: 1.1, 1.2, 1.5, 2.1-2.5, 3.1-3.3, 4.1-4.3, 5.1-5.3,
 *               6.1-6.4, 7.1, 7.3, 7.4, 15.1-15.4, 18.1-18.5
 */

import {
  CellState,
  ConnectFourGrid,
  DiscColor,
  Position,
  ConnectFourMove,
  ConnectFourMoveResult,
  ConnectFourGameState,
  ConnectFourGameStatus,
  ROWS,
  COLS,
  TOTAL_CELLS,
} from './types';

export class ConnectFourEngine {
  private gameState: ConnectFourGameState;
  private stateHistory: ConnectFourGameState[] = [];

  constructor() {
    this.gameState = this.createInitialGameState();
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================

  private createInitialGameState(): ConnectFourGameState {
    return {
      grid: this.createEmptyGrid(),
      currentPlayer: 'red',
      moveHistory: [],
      gameStatus: { type: 'active' },
      moveCount: 0,
      winningCells: null,
    };
  }

  private createEmptyGrid(): ConnectFourGrid {
    const grid: ConnectFourGrid = [];
    for (let row = 0; row < ROWS; row++) {
      grid.push(new Array(COLS).fill(null));
    }
    return grid;
  }

  initializeGame(): void {
    this.gameState = this.createInitialGameState();
    this.stateHistory = [];
  }

  // ==========================================================================
  // State Queries
  // ==========================================================================

  getBoard(): ConnectFourGrid {
    return this.gameState.grid;
  }

  getGameState(): ConnectFourGameState {
    return this.gameState;
  }

  getCurrentPlayer(): DiscColor {
    return this.gameState.currentPlayer;
  }

  getGameStatus(): ConnectFourGameStatus {
    return this.gameState.gameStatus;
  }

  getMoveHistory(): ConnectFourMove[] {
    return this.gameState.moveHistory;
  }

  getMoveCount(): number {
    return this.gameState.moveCount;
  }

  getWinningCells(): Position[] | null {
    return this.gameState.winningCells;
  }

  isGameOver(): boolean {
    return this.gameState.gameStatus.type !== 'active';
  }

  // ==========================================================================
  // Column Validation
  // ==========================================================================

  isValidColumn(column: number): boolean {
    return Number.isInteger(column) && column >= 0 && column < COLS && !this.isColumnFull(column);
  }

  isColumnFull(column: number): boolean {
    if (column < 0 || column >= COLS) return true;
    return this.gameState.grid[ROWS - 1][column] !== null;
  }

  getLowestEmptyRow(column: number): number | null {
    if (column < 0 || column >= COLS) return null;
    for (let row = 0; row < ROWS; row++) {
      if (this.gameState.grid[row][column] === null) {
        return row;
      }
    }
    return null;
  }

  getValidColumns(): number[] {
    const valid: number[] = [];
    for (let col = 0; col < COLS; col++) {
      if (!this.isColumnFull(col)) {
        valid.push(col);
      }
    }
    return valid;
  }

  // ==========================================================================
  // Move Execution
  // ==========================================================================

  dropDisc(column: number): ConnectFourMoveResult {
    // Game must be active
    if (this.gameState.gameStatus.type !== 'active') {
      return {
        success: false,
        error: 'Game is not active',
        gameStatus: this.gameState.gameStatus,
      };
    }

    // Validate column range
    if (!Number.isInteger(column) || column < 0 || column >= COLS) {
      return {
        success: false,
        error: 'Invalid column. Please select a column between 0 and 6.',
        gameStatus: this.gameState.gameStatus,
      };
    }

    // Check if column is full
    if (this.isColumnFull(column)) {
      return {
        success: false,
        error: `Column ${column} is full. Please choose another column.`,
        gameStatus: this.gameState.gameStatus,
      };
    }

    // Save state for undo
    this.pushStateSnapshot();

    // Find lowest empty row
    const row = this.getLowestEmptyRow(column)!;
    const player = this.gameState.currentPlayer;

    // Place disc
    this.gameState.grid[row][column] = player;

    // Record move
    const move: ConnectFourMove = {
      column,
      row,
      player,
      moveNumber: this.gameState.moveCount + 1,
      timestamp: Date.now(),
    };
    this.gameState.moveHistory.push(move);
    this.gameState.moveCount++;

    // Check win condition
    const winStatus = this.checkWinCondition(row, column);
    if (winStatus.type !== 'active') {
      this.gameState.gameStatus = winStatus;
      return {
        success: true,
        move,
        gameStatus: this.gameState.gameStatus,
      };
    }

    // Check draw
    if (this.checkDraw()) {
      this.gameState.gameStatus = { type: 'draw' };
      return {
        success: true,
        move,
        gameStatus: this.gameState.gameStatus,
      };
    }

    // Switch player
    this.gameState.currentPlayer = player === 'red' ? 'yellow' : 'red';

    return {
      success: true,
      move,
      gameStatus: this.gameState.gameStatus,
    };
  }

  // ==========================================================================
  // Win Detection
  // ==========================================================================

  checkWinCondition(lastRow: number, lastCol: number): ConnectFourGameStatus {
    const grid = this.gameState.grid;
    const color = grid[lastRow][lastCol];
    if (!color) return { type: 'active' };

    // Direction vectors: [rowDelta, colDelta]
    const directions: Array<{ dr: number; dc: number; name: 'horizontal' | 'vertical' | 'diagonal-asc' | 'diagonal-desc' }> = [
      { dr: 0, dc: 1, name: 'horizontal' },
      { dr: 1, dc: 0, name: 'vertical' },
      { dr: 1, dc: 1, name: 'diagonal-asc' },
      { dr: 1, dc: -1, name: 'diagonal-desc' },
    ];

    for (const { dr, dc, name } of directions) {
      const cells = this.countConsecutive(lastRow, lastCol, dr, dc, color);
      if (cells.length >= 4) {
        // Store the first 4 winning cells
        this.gameState.winningCells = cells.slice(0, 4);
        return { type: 'win', winner: color, direction: name };
      }
    }

    return { type: 'active' };
  }

  /**
   * Count consecutive same-color cells in both directions from a position.
   * Returns all positions forming the line (including the origin).
   */
  private countConsecutive(
    row: number,
    col: number,
    dr: number,
    dc: number,
    color: CellState
  ): Position[] {
    const grid = this.gameState.grid;
    const positions: Position[] = [];

    // Count in negative direction
    let r = row - dr;
    let c = col - dc;
    const before: Position[] = [];
    while (r >= 0 && r < ROWS && c >= 0 && c < COLS && grid[r][c] === color) {
      before.push({ row: r, col: c });
      r -= dr;
      c -= dc;
    }
    before.reverse();

    // Add origin
    positions.push(...before, { row, col });

    // Count in positive direction
    r = row + dr;
    c = col + dc;
    while (r >= 0 && r < ROWS && c >= 0 && c < COLS && grid[r][c] === color) {
      positions.push({ row: r, col: c });
      r += dr;
      c += dc;
    }

    return positions;
  }

  // ==========================================================================
  // Draw Detection
  // ==========================================================================

  checkDraw(): boolean {
    return this.gameState.moveCount >= TOTAL_CELLS;
  }

  // ==========================================================================
  // Serialization / Deserialization
  // ==========================================================================

  serialize(): string {
    return JSON.stringify(this.gameState);
  }

  deserialize(json: string): ConnectFourGameState | { error: string } {
    try {
      if (!json || typeof json !== 'string' || json.trim() === '') {
        return { error: 'Empty or invalid input' };
      }

      let parsed: any;
      try {
        parsed = JSON.parse(json);
      } catch {
        return { error: 'Invalid JSON format' };
      }

      if (!parsed || typeof parsed !== 'object') {
        return { error: 'Parsed value is not an object' };
      }

      // Validate required fields
      if (!Array.isArray(parsed.grid)) {
        return { error: 'Missing or invalid grid' };
      }
      if (parsed.grid.length !== ROWS) {
        return { error: `Grid must have ${ROWS} rows` };
      }
      for (let r = 0; r < ROWS; r++) {
        if (!Array.isArray(parsed.grid[r]) || parsed.grid[r].length !== COLS) {
          return { error: `Grid row ${r} must have ${COLS} columns` };
        }
        for (let c = 0; c < COLS; c++) {
          const cell = parsed.grid[r][c];
          if (cell !== null && cell !== 'red' && cell !== 'yellow') {
            return { error: `Invalid cell value at [${r}][${c}]: ${cell}` };
          }
        }
      }

      if (parsed.currentPlayer !== 'red' && parsed.currentPlayer !== 'yellow') {
        return { error: 'Invalid currentPlayer' };
      }

      if (!Array.isArray(parsed.moveHistory)) {
        return { error: 'Missing or invalid moveHistory' };
      }

      if (typeof parsed.moveCount !== 'number' || parsed.moveCount < 0) {
        return { error: 'Missing or invalid moveCount' };
      }

      if (!parsed.gameStatus || typeof parsed.gameStatus.type !== 'string') {
        return { error: 'Missing or invalid gameStatus' };
      }

      return parsed as ConnectFourGameState;
    } catch (e) {
      return { error: `Deserialization failed: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  setGameState(state: ConnectFourGameState): void {
    this.gameState = state;
  }

  // ==========================================================================
  // Undo Move
  // ==========================================================================

  undoMove(count: number = 1): boolean {
    if (count < 1) return false;

    const actualCount = Math.min(count, this.stateHistory.length);
    if (actualCount === 0) return false;

    // Pop states
    let restoredState: ConnectFourGameState | undefined;
    for (let i = 0; i < actualCount; i++) {
      restoredState = this.stateHistory.pop();
    }

    if (restoredState) {
      this.gameState = restoredState;
      return true;
    }
    return false;
  }

  private pushStateSnapshot(): void {
    this.stateHistory.push(JSON.parse(JSON.stringify(this.gameState)));
  }
}
